import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock, X } from '@phosphor-icons/react';
import { formatDualCurrency } from '../utils/currency';
import { useCurrency } from '../context/CurrencyContext';
import { getRecentlyViewed, clearRecentlyViewed } from '../utils/recentlyViewed';
import { getCategoryName } from '../utils/categoryNames';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RecentlyViewed = () => {
  const { t } = useTranslation();
  const { secondaryCurrency } = useCurrency();
  const [items, setItems] = useState([]);

  useEffect(() => {
    setItems(getRecentlyViewed());
  }, []);

  if (!items.length) return null;

  const handleClear = () => {
    clearRecentlyViewed();
    setItems([]);
  };

  return (
    <section className="mb-6" data-testid="recently-viewed-strip">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock size={18} className="text-text_muted" weight="bold" />
          <h2 className="text-sm font-semibold text-text_strong tracking-wide">
            {t('recent.title', 'Recently viewed')}
          </h2>
          <span className="text-xs text-text_muted">({items.length})</span>
        </div>
        <button
          onClick={handleClear}
          className="text-xs text-text_muted hover:text-primary flex items-center gap-1"
          data-testid="recent-clear-btn"
        >
          <X size={12} /> {t('recent.clear', 'Clear')}
        </button>
      </div>

      <div
        className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory"
        style={{ scrollbarWidth: 'thin' }}
        data-testid="recent-list"
      >
        {items.map((it) => {
          const price = formatDualCurrency(it.price_mga, secondaryCurrency);
          return (
            <Link
              key={it.listing_id}
              to={`/listings/${it.listing_id}`}
              className="snap-start flex-shrink-0 w-[140px] md:w-[160px] border border-border bg-white rounded-xl overflow-hidden hover:border-primary transition-colors"
              data-testid={`recent-${it.listing_id}`}
            >
              <div className="aspect-[4/3] bg-surface">
                {it.image ? (
                  <img
                    src={`${API}/files/${it.image}`}
                    alt={it.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-text_muted text-xs">
                    {t('common.noImage')}
                  </div>
                )}
              </div>
              <div className="p-2">
                <p className="text-xs font-medium truncate" title={it.title}>{it.title}</p>
                <p className="text-sm font-bold text-primary truncate">{price.mga}</p>
                {it.city && (
                  <p className="text-[10px] text-text_muted truncate">
                    {it.city}
                    {it.category && <> · {getCategoryName(it.category, t)}</>}
                  </p>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};
